import { extractAppConfig } from '@/hooks/use-app-config';
import { match as localeMatcher } from '@formatjs/intl-localematcher';

export enum SupportedLanguageType {
    EN = 'en',
    ES = 'es',
    FR = 'fr',
}

export enum SupportedLocale {
    ENus = 'en-US',
    ENgb = 'en-GB',
    ENca = 'en-CA',
    EN = 'en',
    ES = 'es',
    FR = 'fr',
}

export const defaultLanguage = SupportedLanguageType.EN;
export const defaultLocale = SupportedLocale.EN;

export function getLanguage(
    acceptLanguageHeader?: string | null
): SupportedLanguageType {
    const { NEXT_PUBLIC_SUPPORTED_LOCALES } = extractAppConfig();
    if (!acceptLanguageHeader) {
        return defaultLanguage;
    }

    // Convert the Accept-Language header into an array of locales
    const requestedLocales = acceptLanguageHeader
        ? acceptLanguageHeader
              .split(',')
              .map((lang: string) => lang.split(';')[0].trim()) // Extract just the locale
        : [];

    // Use `localeMatcher` to find the best match
    const matchedLanguage = localeMatcher(
        requestedLocales,
        NEXT_PUBLIC_SUPPORTED_LOCALES,
        defaultLanguage
    );

    return matchedLanguage as SupportedLanguageType;
}

export function getLocale(
    acceptLanguageHeader?: string | null
): SupportedLocale {
    if (!acceptLanguageHeader) {
        return defaultLocale;
    }

    // Convert the Accept-Language header into an array of locales
    const requestedLocales = acceptLanguageHeader
        ? acceptLanguageHeader
              .split(',')
              .map((lang: string) => lang.split(';')[0].trim()) // Extract just the locale
        : [];

    // Use `localeMatcher` to find the best match
    const matchedLocale = localeMatcher(
        requestedLocales,
        Object.values(SupportedLocale),
        defaultLocale
    );

    return matchedLocale as SupportedLocale;
}

export function determineRedirect(
    pathname: string,
    requestLocale: string,
    acceptLanguageHeader: string | null
) {
    const { NEXT_PUBLIC_SUPPORTED_LOCALES } = extractAppConfig();
    const language = getLanguage(acceptLanguageHeader);

    if (language === defaultLanguage) {
        // remove any locale from supported languages
        const regexp = new RegExp(
            `^/(${NEXT_PUBLIC_SUPPORTED_LOCALES.join('|')})`
        );
        const pathContainsLocale = regexp.test(pathname);

        if (!pathContainsLocale) {
            return;
        }

        const newUrl = pathname.replace(regexp, '');
        return newUrl;
    }

    if (language === requestLocale) {
        return;
    }

    return `/${language}${pathname}`;
}
